/**
 * CrudTable — reusable themed data table for the generic-CRUD admin pages
 * (OIDCPAM Gate-6 Phase 4a: Clients/Apps/Resources today; Connectors/Users/
 * Subjects/Tenants/Entitlements/Policies in later Gate-6 phases per the
 * shared constraints doc's Phase-4 directive: "@devnw/data DataTable is
 * RN-only (unusable here). Build ONE reusable local themed table + form").
 *
 * Semantic <table> with scope="col" headers (Th), striped/hover rows, an
 * optional per-row action slot, loading (role="status") / error
 * (role="alert") / empty states, and a TruncText+title ellipsis helper for
 * long cell values — mirrors the a11y discipline of
 * `@devnw/cyber-iam`'s internal `iam/internal/styledShared.ts`
 * (DataTable/Th/Td/TruncText), which is explicitly NOT a public API ("NOT a
 * public API: the dist subpath exists as a tsup fs-discovery build artifact
 * only"), so this file re-derives the same look from the public
 * `@devnw/theme` tokens instead of reaching into that internal module.
 *
 * Column/rowActions shape is intentionally identical to the pre-migration
 * MUI-based `ui/src/resource/ResourceTable.tsx`'s `Column<T>`, so migrating
 * a page off it is a near-mechanical import/type-name swap.
 */
import React, { ReactNode } from 'react'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
import { Icon } from '@devnw/icons'

export interface CrudColumn<T> {
  key: string
  header: string
  render?: (row: T) => ReactNode
  /**
   * When set (and no custom `render` is provided), the plain string value is
   * wrapped in a truncating span with `max-width: {maxWidth}px` and a
   * `title` attribute carrying the full value — the TruncText pattern.
   */
  maxWidth?: number
}

export interface CrudTableProps<T> {
  columns: CrudColumn<T>[]
  items: T[]
  rowActions?: (row: T) => ReactNode
  /** Row key extractor; defaults to `(row as {id}).id`, falling back to the row index. */
  getRowKey?: (row: T, index: number) => string
  /** When true, the body shows a spinner (role="status") instead of rows. */
  loading?: boolean
  /** When set, the body shows this message (role="alert") instead of rows. */
  error?: string | null
  /** Empty-state message shown when `items` is empty and not loading/erroring. */
  emptyMessage?: string
  /** Accessible name for the table, rendered as a visually-hidden <caption>. */
  caption?: string
}

const TableScroll = styled.div`
  align-self: stretch;
  overflow-x: auto;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.sm ?? '0.875rem'};
  color: ${({ theme }) => asUnified(theme).text?.primary ?? '#0f172a'};
`

/** Visually hidden but present in the accessibility tree (standard sr-only clip pattern). */
const VisuallyHiddenCaption = styled.caption`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`

const Th = styled.th.attrs({ scope: 'col' })`
  text-align: left;
  padding: ${({ theme }) =>
    `${ut(asUnified(theme)).space('xs')} ${ut(asUnified(theme)).space('sm')}`};
  border-bottom: 2px solid ${({ theme }) => ut(asUnified(theme)).borderColor() ?? '#e2e8f0'};
  background: ${({ theme }) => asUnified(theme).bg?.subtle ?? asUnified(theme).bg?.surface ?? '#f8fafc'};
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.xs ?? '0.75rem'};
  font-weight: 700;
  color: ${({ theme }) => asUnified(theme).text?.secondary ?? '#64748b'};
  white-space: nowrap;
`

const Tr = styled.tr`
  &:nth-child(even) {
    background: ${({ theme }) => asUnified(theme).bg?.subtle ?? 'transparent'};
  }

  &:hover {
    background: ${({ theme }) => `${asUnified(theme).colors?.primary ?? '#1a56db'}0d`};
  }
`

const Td = styled.td`
  padding: ${({ theme }) =>
    `${ut(asUnified(theme)).space('xs')} ${ut(asUnified(theme)).space('sm')}`};
  border-bottom: 1px solid ${({ theme }) => ut(asUnified(theme)).borderColor() ?? '#e2e8f0'};
  vertical-align: top;
`

const ActionsTd = styled(Td)`
  white-space: nowrap;
`

const StatusTd = styled.td`
  padding: ${({ theme }) => ut(asUnified(theme)).space('xl')} ${({ theme }) => ut(asUnified(theme)).space('sm')};
  text-align: center;
`

const StatusInline = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => ut(asUnified(theme)).space('xs')};
`

/**
 * Truncating inline text: `display: inline-block` so `text-overflow`
 * actually applies (an inline span silently no-ops). ALWAYS pair with a
 * `title` attribute carrying the full value — enforced by CrudTable's
 * default cell renderer below, exported for pages that want the same
 * truncation inside a custom `render`.
 */
export const CrudTruncText = styled.span<{ $maxWidth: number }>`
  display: inline-block;
  max-width: ${({ $maxWidth }) => $maxWidth}px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
`

const SpinnerRing = styled.div<{ $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  border: 3px solid ${({ theme }) => `${asUnified(theme).colors?.primary ?? '#1a56db'}33`};
  border-top-color: ${({ theme }) => asUnified(theme).colors?.primary ?? '#1a56db'};
  animation: crud-table-spin 0.9s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @keyframes crud-table-spin {
    to {
      transform: rotate(360deg);
    }
  }
`

const EmptyText = styled.span`
  color: ${({ theme }) => asUnified(theme).text?.secondary ?? '#64748b'};
  font-style: italic;
`

const ErrorText = styled.span`
  color: ${({ theme }) => asUnified(theme).colors?.danger ?? '#991b1b'};
`

function defaultRowKey<T>(row: T, index: number): string {
  const maybeId = (row as { id?: unknown }).id
  return typeof maybeId === 'string' || typeof maybeId === 'number'
    ? String(maybeId)
    : String(index)
}

export const CrudTable = <T extends object>({
  columns,
  items,
  rowActions,
  getRowKey = defaultRowKey,
  loading = false,
  error = null,
  emptyMessage = 'No items to display.',
  caption,
}: CrudTableProps<T>): JSX.Element => {
  const allColumns = rowActions
    ? [...columns, { key: '__actions__', header: 'Actions' } as CrudColumn<T>]
    : columns

  const renderCellValue = (col: CrudColumn<T>, row: T): ReactNode => {
    if (col.render) return col.render(row)
    const raw = (row as Record<string, unknown>)[col.key]
    const text = raw === null || raw === undefined ? '' : String(raw)
    if (col.maxWidth) {
      return (
        <CrudTruncText $maxWidth={col.maxWidth} title={text}>
          {text}
        </CrudTruncText>
      )
    }
    return text
  }

  const renderBody = (): ReactNode => {
    if (loading) {
      return (
        <tr>
          <StatusTd colSpan={allColumns.length}>
            <SpinnerRing
              role="status"
              aria-live="polite"
              aria-label="Loading"
              $size={28}
            />
          </StatusTd>
        </tr>
      )
    }

    if (error) {
      return (
        <tr>
          <StatusTd colSpan={allColumns.length}>
            <ErrorText role="alert">{error}</ErrorText>
          </StatusTd>
        </tr>
      )
    }

    if (items.length === 0) {
      return (
        <tr>
          <StatusTd colSpan={allColumns.length}>
            <StatusInline>
              <Icon name="circle-info" ariaHidden size="sm" />
              <EmptyText>{emptyMessage}</EmptyText>
            </StatusInline>
          </StatusTd>
        </tr>
      )
    }

    return items.map((row, index) => (
      <Tr key={getRowKey(row, index)}>
        {columns.map((col) => (
          <Td key={col.key}>{renderCellValue(col, row)}</Td>
        ))}
        {rowActions && <ActionsTd>{rowActions(row)}</ActionsTd>}
      </Tr>
    ))
  }

  return (
    <TableScroll>
      <Table>
        {caption && <VisuallyHiddenCaption>{caption}</VisuallyHiddenCaption>}
        <thead>
          <Tr>
            {allColumns.map((col) => (
              <Th key={col.key}>{col.header}</Th>
            ))}
          </Tr>
        </thead>
        <tbody>{renderBody()}</tbody>
      </Table>
    </TableScroll>
  )
}
